import { 
  getStartAndEndOfWeek, 
  weekIdToTimestamp as weekIdToTimestampFromDateUtils,
  capWeekIdToValidWeek
} from 'src/utils/dateUtils';
import { SplineChartV2DataPoint, SplineChartV2Series, WeekMeta } from './SplineChartV2';

/**
 * Extended data point with computed metadata (used internally)
 */
export interface SplineChartV2DataPointWithMeta extends SplineChartV2DataPoint {
  meta: WeekMeta;
}

/**
 * Converts a weekId to metadata including timestamps and formatted labels.
 * Uses SDS-based utilities to properly handle week boundaries (e.g., week 53).
 */
export function weekIdToMeta(weekId: number): WeekMeta {
  // First, cap the weekId to a valid week in case the year doesn't have week 53
  const validWeekId = capWeekIdToValidWeek(weekId);
  
  const year = Math.floor(validWeekId / 100);
  const week = validWeekId % 100;

  // Use SDS-based getStartAndEndOfWeek for proper date handling
  const { startDate, endDate } = getStartAndEndOfWeek(validWeekId, false);
  
  // Use the timestamp from the validated weekId
  const ts = weekIdToTimestampFromDateUtils(validWeekId) ?? 0;

  return {
    year,
    week,
    weekLabel: String(week),
    ts,
    isoDate: endDate ? `${endDate.slice(0, 4)}-${endDate.slice(4, 6)}-${endDate.slice(6, 8)}` : ''
  };
}

/**
 * Formats an ISO date string (YYYY-MM-DD) to "M/D" format (no leading zeros)
 */
export function formatIsoDateToMMDD(isoDate: string): string {
  if (!isoDate || isoDate.length < 10) return '';
  const month = parseInt(isoDate.slice(5, 7), 10);
  const day = parseInt(isoDate.slice(8, 10), 10);
  return `${month}/${day}`;
}

/**
 * Converts a weekId to a timestamp (end of week).
 * Used for formatting x-axis labels.
 * Delegates to the SDS-based implementation in dateUtils.
 */
export function weekIdToTimestamp(weekId: number): number {
  // First cap to valid week, then convert to timestamp
  const validWeekId = capWeekIdToValidWeek(weekId);
  return weekIdToTimestampFromDateUtils(validWeekId) ?? 0;
}

/**
 * Fills category positions sequentially with data points.
 * Data points are mapped 1:1 to category positions by index.
 *
 * @param data - The data points (should be sorted by weekId)
 * @param categoryCount - Number of category positions to fill
 */
export function fillCategoryData(
  data: SplineChartV2DataPoint[],
  categoryCount: number
): (SplineChartV2DataPointWithMeta | null)[] {
  return Array.from({ length: categoryCount }, (_, idx) => {
    const dataPoint = data[idx];
    if (dataPoint) {
      return {
        ...dataPoint,
        meta: weekIdToMeta(dataPoint.weekId)
      };
    }
    return null;
  });
}

/**
 * Utility to create a series from raw data keyed by weekId
 */
export function createSeriesFromWeekIdMap(
  id: string,
  name: string,
  dataByWeekId: Record<number, number | null>,
  options?: Partial<SplineChartV2Series>
): SplineChartV2Series {
  return {
    id,
    name,
    data: Object.entries(dataByWeekId).map(([weekId, value]) => ({
      weekId: Number(weekId),
      value
    })),
    ...options
  };
}

/**
 * Adds metadata to data points
 */
export function addMetaToDataPoints(data: SplineChartV2DataPoint[]): SplineChartV2DataPointWithMeta[] {
  return data.map((point) => ({
    ...point,
    meta: weekIdToMeta(point.weekId)
  }));
}

/**
 * Merges comparison and primary data into one continuous series for prior period mode
 */
export function mergeComparisonAndPrimaryData(
  comparisonData: SplineChartV2DataPoint[],
  primaryData: SplineChartV2DataPoint[]
): SplineChartV2DataPointWithMeta[] {
  const comparisonWithMeta = addMetaToDataPoints(comparisonData);
  const primaryWithMeta = addMetaToDataPoints(primaryData);
  return [...comparisonWithMeta, ...primaryWithMeta];
}
